import React, { useRef } from 'react';
import { LayoutDashboard, Heart, Mountain, FolderOpen, Settings, LogOut, User, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import animatedLogo from '../assets/animated-logo.gif';

const Sidebar = ({ activeTab, setActiveTab }) => {
  // Ref to reference the animated logo element
  const logoRef = useRef(null);

  /**
   * Handle logo click for interaction
   * GIFs auto-loop so no manual control needed
   */
  const handleLogoClick = () => {
    // Optional: Add click feedback or other interactions
    if (logoRef.current) {
      // Could add click animations or effects here if needed
    }
  };

  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();
  const { user, logout } = useAuth();

  const baseTabs = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, active: true },
    { id: 'fleet', name: 'Fleet Health', icon: Heart, active: true },
    { id: 'terrain', name: 'Terrain Intelligence', icon: Mountain, active: true },
    { id: 'missions', name: 'Saved Missions', icon: FolderOpen, active: true },
    { id: 'settings', name: 'Settings', icon: Settings, active: true },
  ];

  // Add Users tab for admins only
  const tabs = user?.role === 'admin'
    ? [...baseTabs, { id: 'users', name: 'User Management', icon: Users, active: true }]
    : baseTabs;

  return (
    <div className={`w-64 ${themeClasses.sidebar} border-r ${themeClasses.border} h-screen flex flex-col`}>
      {/* Logo Section */}
      <div className={`p-6 border-b ${themeClasses.border}`}>
        <div className="flex flex-col items-center mb-4">
          {/* Animated Logo Container - 150px circular with theme-aware border */}
          <div
            onClick={handleLogoClick}
            className={`mb-2 rounded-full border-4 ${theme === 'light' ? 'border-black' : themeClasses.border} cursor-pointer transform transition-all duration-300 hover:scale-105 hover:border-military-600/50 hover:shadow-lg hover:shadow-military-700/30`}
            style={{ width: '150px', height: '150px' }}
          >
            {/*
              Animated logo GIF - auto-loops continuously
              Features:
              - Better quality than video compression
              - Auto-plays and loops seamlessly
              - No manual controls needed
              - Better browser compatibility
            */}
            <img
              ref={logoRef}
              src={animatedLogo}
              alt="FracAdapt Logo"
              className="w-full h-full object-cover rounded-full"
              onError={(e) => {
                console.log('Animated logo failed to load, showing fallback');
                e.target.parentElement.style.display = 'none';
                e.target.parentElement.nextSibling.style.display = 'flex';
              }}
            />
          </div>
          {/* Fallback Logo - displays if GIF fails to load */}
          <div
            className={`w-36 h-36 mb-2 bg-military-600 rounded-full flex items-center justify-center border-4 ${theme === 'light' ? 'border-black' : themeClasses.border} shadow-lg shadow-military-700/30`}
            style={{ display: 'none' }}
          >
            <span className={`${themeClasses.textPrimary} font-bold text-4xl`}>FA</span>
          </div>
        </div>
        <h1 className={`text-2xl font-bold ${themeClasses.textPrimary} text-center`}>FracAdapt</h1>
        <p className={`text-xs ${themeClasses.textMuted} mt-1 text-center`}>Fleet Management System</p>
      </div>

      {/* Navigation Tabs */}
      <nav className="flex-1 p-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const isDisabled = !tab.active;

          return (
            <button
              key={tab.id}
              onClick={() => tab.active && setActiveTab(tab.id)}
              disabled={isDisabled}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-all ${isActive
                ? 'bg-military-600 text-white shadow-lg'
                : isDisabled
                  ? `${themeClasses.textMuted} cursor-not-allowed`
                  : `${themeClasses.textSecondary} hover:bg-military-700/30 hover:text-white`
                }`}
            >
              <Icon size={20} />
              <span className="font-medium">{tab.name}</span>
              {isDisabled && (
                <span className={`ml-auto text-xs ${themeClasses.card} px-2 py-0.5 rounded`}>Soon</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User info & Logout */}
      <div className={`p-4 border-t ${themeClasses.border}`}>
        {user && (
          <div className={`flex items-center gap-2 mb-3 px-2`}>
            <User size={16} className={themeClasses.textMuted} />
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${themeClasses.textPrimary} truncate`}>{user.userId}</p>
              <p className={`text-xs ${themeClasses.textMuted} truncate`}>{user.role}</p>
            </div>
          </div>
        )}
        <button
          onClick={logout}
          className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${themeClasses.textMuted} hover:bg-red-500/20 hover:text-red-400 transition-all`}
        >
          <LogOut size={16} />
          <span>Sign Out</span>
        </button>
        <div className={`mt-3 text-xs ${themeClasses.textMuted} px-2`}>
          <p>Version 1.0.0</p>
          <p className="mt-1">&copy; 2025 FracAdapt</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
